import { DatePipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Injector,
    Input
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CardComponent } from "@components/cards/card/card.component";
import { CardsComponent } from "@components/cards/cards.component";
import { BlockComponent } from "@components/container-block/block.component";
import { IconComponent } from "@components/icon/icon.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { MenuButtonComponent } from "@components/menu-button/menu-button.component";
import { SpanComponent } from "@components/span/span.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { NgbModalOptions, NgbPopover, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { firstValueFrom, Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, DigitalSignaturDeltagareStatus, DigitalSignaturStatus } from "~app/common/models/constants";
import { Utils } from "~app/common/services/utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import {
    RaskSigneringStatus,
    VirkeskopskontraktDigitalSigneringDeltagareViewModel,
    VirkeskopskontraktDigitalSigneringViewModel
} from "~app/features/virkeskop/models/kontrakt-models";
import { DigitalSigneringMediatorService } from "~app/features/virkeskop/services/digitalsignering-mediator.service";
import { KontraktDokumentService } from "~app/features/virkeskop/services/kontrakt-dokument.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { KontraktDigitalsigneringProlongDialogComponent } from "../kontrakt-digitalsignering-prolong-dialog/kontrakt-digitalsignering-prolong-dialog.component";

@Component({
    selector: "rask-kontrakt-digitalsignering-item",
    templateUrl: "./kontrakt-digitalsignering-item.component.html",
    styleUrl: "./kontrakt-digitalsignering-item.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardsComponent,
        CardComponent,
        ActionButtonComponent,
        IconComponent,
        SpanComponent,
        NgbTooltip,
        NgbPopover,
        MenuButtonComponent,
        TogglePanelComponent,
        NgClass,
        BlockComponent,
        ListComponent,
        ListColumnDirective,
        DatePipe
    ]
})
export class KontraktDigitalsigneringItemComponent {
    @Input() readonly: boolean;
    @Input() signeringInfo: VirkeskopskontraktDigitalSigneringViewModel;

    protected infoIcon = "far fa-exclamation-triangle";
    protected kontraktId$: Observable<number>;
    protected signatureIcon = "far fa-file-signature";

    private readonly digitalSigneringMediator = inject(DigitalSigneringMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly localInjector = inject(Injector);
    private readonly raskModalService = inject(RaskModalService);
    private readonly kontraktDokumentService = inject(KontraktDokumentService);

    constructor() {
        this.kontraktId$ = this.kontraktMediatorService.kontrakt$.pipe(
            map(kontrakt => kontrakt.id),
            shareReplay(1)
        );
    }

    protected openDokument(kontraktId: number, dokumentId?: number) {
        this.kontraktDokumentService.getDokumentForKontraktDownload(kontraktId, dokumentId).subscribe((blob: Blob) => {
            const url = URL.createObjectURL(blob);
            window.open(url);
        });
        return false;
    }

    protected downloadDokument(kontraktId: number, dokumentId?: number, fileName?: string) {
        this.kontraktDokumentService.getDokumentForKontraktDownload(kontraktId, dokumentId).subscribe((blob: Blob) => {
            Utils.downloadBlob(blob, fileName);
        });
        return false;
    }

    protected canUpdateStatus(): boolean {
        return this.signeringInfo.status !== RaskSigneringStatus.KontraktNedladdat;
    }
    protected canProlong(): boolean {
        return this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.signed &&
            this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.declined;
    }

    protected canSendReminder(): boolean {
        return this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.signed &&
            this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.declined &&
            this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.overdue;
    }

    protected canWithdraw(): boolean {
        return this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.signed &&
            this.signeringInfo.signingProviderStatus !== DigitalSignaturStatus.overdue;
    }

    protected getDeltagareSigneringStatus(deltagare: VirkeskopskontraktDigitalSigneringDeltagareViewModel): string {
        if (ObjectUtils.hasValue(deltagare.emailDeliveryError)) {
            return `${this.infoIcon} error`;
        }

        switch (deltagare.status) {
            case DigitalSignaturDeltagareStatus.unknown: {
                return `${this.signatureIcon} warning`;
            }
            case DigitalSignaturDeltagareStatus.signed: {
                return "far fa-check success";
            }
            case DigitalSignaturDeltagareStatus.declined: {
                return `${this.infoIcon} error`;
            }
            default:
                return `${this.signatureIcon} warning`;
        }
    }

    protected getName(party: VirkeskopskontraktDigitalSigneringDeltagareViewModel) {
        return ObjectUtils.hasValue(party.ansvarigVirkeskopareId) ? party.ansvarigVirkeskopareNamn : party.leverantorNamn;
    }

    protected getStatusIcon(): string {
        switch (this.signeringInfo.signingProviderStatus) {
            case DigitalSignaturStatus.draft: {
                return `${this.signatureIcon} warning`;
            }
            case DigitalSignaturStatus.pending: {
                return `${this.signatureIcon} warning`;
            }
            case DigitalSignaturStatus.canceled: {
                return `${this.signatureIcon} error`;
            }
            case DigitalSignaturStatus.overdue: {
                return `${this.signatureIcon} error`;
            }
            case DigitalSignaturStatus.signed: {
                return "far fa-check success";
            }
            case DigitalSignaturStatus.declined: {
                return `${this.infoIcon} error`;
            }
            default:
                return this.signatureIcon;
        }
    }

    protected getStatusPopOverText(deltagare: VirkeskopskontraktDigitalSigneringDeltagareViewModel): string {
        if (ObjectUtils.hasValue(deltagare.emailDeliveryError)) {
            return deltagare.emailDeliveryError;
        }
        if (ObjectUtils.hasValue(deltagare.avvisatMeddelande)) {
            return deltagare.avvisatMeddelande;
        }
        return undefined;
    }

    protected isSigned(): boolean {
        return this.signeringInfo.signingProviderStatus === DigitalSignaturStatus.signed;
    }

    protected prolongSigning() {
        const options = {
            keyboard: false,
            backdrop: "static",
            size: "md",
            injector: this.localInjector
        } as NgbModalOptions;
        this.raskModalService.openAsync(
            "Förläng förfallodatum",
            KontraktDigitalsigneringProlongDialogComponent,
            undefined,
            undefined,
            this.signeringInfo,
            options
        ).subscribe(res => {
            if (res === ConfirmDialogResponse.cancel) {
                return;
            }
            this.prolong(res);
        }
        );
    }

    protected sendReminder() {
        this.digitalSigneringMediator.sendReminder(this.signeringInfo.id);
    }

    protected showEndDate(): boolean {
        return this.signeringInfo.status === RaskSigneringStatus.Skickat;
    }

    protected updateStatus() {
        this.digitalSigneringMediator.updateSigningStatus(this.signeringInfo.id);
    }

    protected async withdrawDocument() {
        const answer: string = await firstValueFrom(
            this.raskModalService.confirm(new ModalSettings("Varning", "Vill du verkligen återkalla signeringen?")));

        if (answer === ConfirmDialogResponse.cancel) {
            return;
        }

        this.digitalSigneringMediator.withdrawDocument(this.signeringInfo.id);
    }

    private prolong(antalDagar: number) {
        if (antalDagar) {
            this.digitalSigneringMediator.prolongSigning(this.signeringInfo.id, antalDagar);
        }
    }
}
